import { useMemo } from 'react';
import { useFlattenedOrders, useOpenOrders } from '../hooks/useOrders';
import { Gwei } from '../lib/gwei';
import { getGroupedPrice } from '../lib/orderbookHelpers';
import { OrderSide } from '../types/generated';
import { useActiveSelectedSubaccount } from './useSelectedSubaccount';

/**
 * Hook to get the price groups at which the selected subaccount has open orders
 * at, split by bids and asks.
 */
export const useOpenOrderPrices = (productId: string | undefined, grouping: string) => {
  const selectedSubaccount = useActiveSelectedSubaccount();
  const { data: orderData } = useOpenOrders({
    subaccountId: selectedSubaccount?.id,
    productIds: productId ? [productId] : undefined,
  });
  const orders = useFlattenedOrders(orderData?.pages);

  return useMemo(() => {
    const askPrices = new Set<bigint>();
    const bidPrices = new Set<bigint>();
    const groupSize = Gwei.toGwei(grouping);

    orders.forEach((order) => {
      const price = order.price;

      if (order.side === OrderSide.BUY) {
        bidPrices.add(getGroupedPrice(price, groupSize, 'bid'));
      } else {
        askPrices.add(getGroupedPrice(price, groupSize, 'ask'));
      }
    });

    return { askPrices, bidPrices };
  }, [orders, grouping]);
};
